import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [countdown, setCountdown] = useState(3);
  const calledRef = useRef(false);

  useEffect(() => {
    if (!token) { setStatus("error"); setMessage("No verification token found."); return; }
    if (calledRef.current) return;
    calledRef.current = true;
    api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(() => setStatus("success"))
      .catch((err) => {
        console.error("Verify email error:", err?.response?.status, err?.response?.data);
        setStatus("error");
        setMessage(err?.response?.data?.detail || "Invalid or expired link.");
      });
  }, [token]);

  // Auto-redirect to /login after 3s on success
  useEffect(() => {
    if (status !== "success") return;
    if (countdown <= 0) { nav("/login", { replace: true }); return; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [status, countdown, nav]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-6">
      <div className="w-full max-w-md text-center">
        <Logo size="md" />
        <div className="mt-12 flex justify-center">
          {status === "loading" && <Loader2 className="h-12 w-12 text-[#C86B53] animate-spin" />}
          {status === "success" && (
            <div className="h-16 w-16 rounded-full bg-[#E8F5EE] flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-[#2E9E5B]" />
            </div>
          )}
          {status === "error" && (
            <div className="h-16 w-16 rounded-full bg-[#FEF2F2] flex items-center justify-center">
              <XCircle className="h-8 w-8 text-[#B74F4F]" />
            </div>
          )}
        </div>

        {status === "loading" && <p className="mt-6 text-[#5C5F62]">Verifying your email…</p>}

        {status === "success" && (
          <>
            <h1 className="mt-6 text-3xl font-display font-semibold tracking-tight">Email verified!</h1>
            <p className="mt-3 text-[#5C5F62]">Your account is now active.</p>
            <p className="mt-2 text-sm text-[#8A8D91]">Redirecting to login in {countdown}s…</p>
            <Link to="/login" className="mt-6 inline-block neivai-btn-primary px-8">
              Go to login now
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <h1 className="mt-6 text-3xl font-display font-semibold tracking-tight">Verification failed</h1>
            <p className="mt-3 text-[#5C5F62]">{message}</p>
            <Link to="/login" className="mt-8 block text-sm text-[#5C5F62] hover:text-[#1A1C1E]">
              Back to login
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
